import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Icon from '../../../components/AppIcon';

const EMPTY_RECOMMENDED_CODE = '// AI Optimized solution would appear here';

const hasUsefulContent = (value) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    return trimmed !== '' && trimmed !== EMPTY_RECOMMENDED_CODE;
};

const CodeReviewSection = ({ snippets, codeFeedback, codeSuggestions, codeRating, codeStrengths, codeWeaknesses, problemReviews }) => {
    const reviews = (problemReviews && problemReviews.length > 0) ? problemReviews : [
        {
            problemTitle: snippets?.[0]?.title || "Technical Implementation",
            focus: snippets?.[0]?.focus || "Efficiency & Reliability Focus",
            code: snippets?.[0]?.code || "",
            recommendedCode: codeSuggestions || EMPTY_RECOMMENDED_CODE,
            modelSolution: "",
            rating: codeRating || 0,
            feedback: codeFeedback || "Overall good implementation with room for minor optimizations.",
            strengths: codeStrengths || [],
            weaknesses: codeWeaknesses || [],
            language: snippets?.[0]?.language || 'JS',
            difficulty: '',
            timeComplexity: '',
            spaceComplexity: '',
        }
    ];

    if (!reviews || reviews.length === 0) return null;

    const getDifficultyStyle = (d) => {
        const lower = (d || '').toLowerCase();
        if (lower === 'easy') return { color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', label: 'Easy' };
        if (lower === 'medium') return { color: 'text-amber-600', bg: 'bg-amber-50', border: 'border-amber-100', label: 'Medium' };
        if (lower === 'hard') return { color: 'text-red-600', bg: 'bg-red-50', border: 'border-red-100', label: 'Hard' };
        return null;
    };

    return (
        <div className="space-y-8 mt-8">
            <div className="flex items-center gap-2">
                <Icon name="List" size={16} className="text-emerald-500" />
                <h3 className="text-sm font-semibold text-slate-900 tracking-wider">Detailed Question Analysis</h3>
            </div>

            {reviews.map((review, index) => (
                <ReviewCard key={index} review={review} index={index} getDifficultyStyle={getDifficultyStyle} />
            ))}
        </div>
    );
};

const ReviewCard = ({ review, index, getDifficultyStyle }) => {
    const navigate = useNavigate();
    const difficulty = getDifficultyStyle(review.difficulty);
    // Detect behavioral questions — no language or code, or explicitly marked
    const isBehavioral = (!review.language || review.language === '' || review.language === 'N/A')
      && (!review.code && !review.modelSolution);
    const hasUserAnswer = hasUsefulContent(review.code || review.answer || '');
    const hasModelAnswer = hasUsefulContent(review.modelSolution || review.modelAnswer || review.recommendedCode || '');
    const [activeTab, setActiveTab] = useState(isBehavioral && !hasUserAnswer && hasModelAnswer ? 'model' : 'solution');
    const [isCopied, setIsCopied] = useState(false);

    return (
        <div className="bg-white border border-slate-100 rounded-[1.5rem] p-6 shadow-sm relative overflow-hidden">
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center border border-emerald-100">
                        <Icon name="Check" size={16} className="text-emerald-500" />
                    </div>
                    <div>
                        <div className="flex items-center gap-2 flex-wrap">
                            <h4 className="text-lg font-semibold text-slate-900 leading-none">
                                Question {index + 1}: {review.problemTitle}
                            </h4>
                            {difficulty && (
                                <span className={`text-[8px] font-semibold ${difficulty.color} ${difficulty.bg} ${difficulty.border} border px-2 py-0.5 rounded-full tracking-widest`}>
                                    {difficulty.label}
                                </span>
                            )}
                        </div>
                        <div className="flex items-center gap-3 mt-1.5">
                            {review.timeComplexity && (
                                <span className="text-[9px] font-medium text-slate-400">
                                    Time: <span className="text-slate-600">{review.timeComplexity}</span>
                                </span>
                            )}
                            {review.spaceComplexity && (
                                <span className="text-[9px] font-medium text-slate-400">
                                    Space: <span className="text-slate-600">{review.spaceComplexity}</span>
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {!isBehavioral && (
                        <span className="text-[9px] font-medium text-slate-400 tracking-widest">{review.language}</span>
                    )}
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                {/* Main Content Area (40%) */}
                <div className="lg:col-span-2 space-y-4">
                    {/* Tabs */}
                    <div className="flex items-center gap-1.5 border-b border-slate-100 pb-1.5">
                        <button
                            onClick={() => setActiveTab('solution')}
                            className={`px-3 py-1.5 rounded-lg text-[10px] font-medium tracking-widest transition-all ${activeTab === 'solution' ? 'bg-emerald-50 text-emerald-600 border border-emerald-100' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            {isBehavioral ? 'Your Answer' : 'Your Solution'}
                        </button>
                        <button
                            onClick={() => setActiveTab('model')}
                            className={`px-3 py-1.5 rounded-lg text-[10px] font-medium tracking-widest transition-all ${activeTab === 'model' ? 'bg-sky-50 text-sky-600 border border-sky-100' : 'text-slate-400 hover:text-slate-600'}`}
                        >
                            <span className="flex items-center gap-1">
                                <Icon name="Sparkles" size={10} />
                                {isBehavioral ? 'Model Answer' : 'Optimal Solution'}
                            </span>
                        </button>
                    </div>

                    {/* Content Block */}
                    <div className={`bg-slate-50 rounded-xl p-4 ${isBehavioral ? 'text-sm leading-relaxed text-slate-700' : 'font-mono text-[12px] leading-relaxed text-slate-700'} overflow-x-auto min-h-[300px] flex flex-col`}>
                        {(() => {
                            const codeContent = activeTab === 'solution'
                              ? (review.code || review.answer || '')
                              : (review.modelSolution || review.modelAnswer || review.recommendedCode || '');
                            if (!hasUsefulContent(codeContent)) {
                                const title = activeTab === 'model'
                                  ? (isBehavioral ? 'Model answer is being refined' : 'Model solution not available')
                                  : (isBehavioral ? 'Your answer was reviewed from the transcript' : 'No code was submitted');
                                const message = activeTab === 'solution' && isBehavioral
                                  ? 'For this non-coding question, the report stores your evaluation from the conversation instead of a separate answer block. Use the transcript and AI analysis on the right to review what was captured.'
                                  : 'We could not find content for this section in the saved report.';
                                return (
                                    <div className="flex flex-col items-center justify-center flex-1 gap-3 text-center px-6 py-10">
                                        <div className="w-12 h-12 rounded-2xl bg-white border border-slate-200 text-slate-500 flex items-center justify-center shadow-sm">
                                            <Icon name={isBehavioral ? 'MessageSquareText' : 'Code'} size={22} />
                                        </div>
                                        <div className="space-y-1 max-w-sm">
                                            <h5 className="text-sm font-semibold text-slate-900 tracking-tight">{title}</h5>
                                            <p className="text-xs font-medium text-slate-500 leading-relaxed">{message}</p>
                                        </div>
                                        {activeTab === 'solution' && isBehavioral && (
                                            <button
                                                onClick={() => document.getElementById('session-transcript')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                                                className="mt-1 inline-flex items-center gap-1.5 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-[10px] font-medium tracking-widest text-emerald-600 shadow-sm hover:border-emerald-200 hover:bg-emerald-50 transition-all"
                                            >
                                                <Icon name="MessagesSquare" size={12} />
                                                View Transcript
                                            </button>
                                        )}
                                    </div>
                                );
                            }
                            return (
                                <div className="relative w-full flex-1 group/codeblock">
                                    {/* Action Buttons */}
                                    <div className="absolute top-2 right-2 flex items-center gap-2 opacity-0 group-hover/codeblock:opacity-100 transition-opacity z-10">
                                        <button
                                            onClick={() => {
                                                navigator.clipboard.writeText(codeContent);
                                                setIsCopied(true);
                                                setTimeout(() => setIsCopied(false), 2000);
                                            }}
                                            className="bg-white/90 backdrop-blur-sm border border-slate-200 text-slate-600 hover:text-emerald-600 hover:border-emerald-200 shadow-sm px-2.5 py-1.5 flex items-center gap-1.5 rounded-lg text-[10px] font-medium tracking-widest transition-all"
                                        >
                                            <Icon name={isCopied ? "Check" : "Copy"} size={12} />
                                            {isCopied ? "Copied!" : "Copy"}
                                        </button>
                                        
                                        {/* Show Play button only for coding solutions */}
                                        {!isBehavioral && (
                                            <button
                                                onClick={() => {
                                                    navigate('/sandbox', { state: { code: codeContent, language: review.language } });
                                                }}
                                                className="bg-emerald-500 text-white shadow-sm shadow-emerald-500/20 hover:bg-emerald-400 px-2.5 py-1.5 flex items-center gap-1.5 rounded-lg text-[10px] font-medium tracking-widest transition-all"
                                            >
                                                <Icon name="Play" size={12} className="fill-white" />
                                                Open Sandbox
                                            </button>
                                        )}
                                    </div>

                                    {isBehavioral ? (
                                        <div className="prose prose-slate max-w-none prose-p:my-2 prose-strong:text-slate-950 prose-ol:my-3 prose-ul:my-3 prose-li:my-1 prose-li:marker:text-emerald-500">
                                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                                {codeContent}
                                            </ReactMarkdown>
                                        </div>
                                    ) : (
                                        <pre className="w-full">
                                            <code>{codeContent}</code>
                                        </pre>
                                    )}
                                </div>
                            );
                        })()}
                    </div>
                </div>

                {/* Sidebar (60%) */}
                <div className="lg:col-span-3 space-y-6">
                    {/* Quality Score */}
                    <div className="bg-white border border-slate-100 rounded-2xl p-4 shadow-xl shadow-slate-200/40 flex items-center justify-between">
                        <div>
                            <p className="text-[9px] font-medium text-slate-400 tracking-widest mb-0.5">Quality Score</p>
                            <h5 className="text-lg font-semibold text-slate-900">
                                {review.rating >= 80 ? 'Exceptional' : review.rating >= 60 ? 'Great Effort' : review.rating >= 40 ? 'Needs Work' : 'Poor'}
                            </h5>
                        </div>
                        <div className={`relative w-12 h-12 flex items-center justify-center border-4 rounded-full ${
                            review.rating >= 80 ? 'border-emerald-500' : review.rating >= 60 ? 'border-sky-500' : review.rating >= 40 ? 'border-amber-500' : 'border-red-500'
                        }`}>
                            <span className="text-sm font-semibold text-slate-900">{review.rating}%</span>
                        </div>
                    </div>

                    <div className="space-y-3">
                        <div className="flex items-center gap-1.5">
                            <Icon name="Sparkles" size={12} className="text-emerald-500" />
                            <span className="text-xs font-semibold text-emerald-600 tracking-widest">AI Analysis</span>
                        </div>
                        <p className="text-sm text-slate-600 font-normal leading-relaxed italic">
                            {review.feedback}
                        </p>
                    </div>

                    {/* Strong Points */}
                    {review.strengths?.length > 0 && (
                        <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-4 space-y-3">
                            <p className="text-[10px] font-medium text-emerald-600 tracking-widest">Strong Points</p>
                            <ul className="space-y-2 text-sm font-normal text-slate-700">
                                {review.strengths.slice(0, 4).map((s, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                        <Icon name="Check" size={12} className="text-emerald-500 mt-0.5 shrink-0" />
                                        <span>{s}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Improvements */}
                    {review.weaknesses?.length > 0 && (
                        <div className="bg-amber-50/50 border border-amber-100 rounded-xl p-4 space-y-3">
                            <p className="text-[10px] font-medium text-amber-600 tracking-widest">Improvements</p>
                            <ul className="space-y-2 text-sm font-normal text-slate-700">
                                {review.weaknesses.slice(0, 4).map((w, idx) => (
                                    <li key={idx} className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-1.5 shrink-0" />
                                        <span>{w}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CodeReviewSection;
